import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MdSearch, MdSupportAgent } from 'react-icons/md';
import { useEngineerRemoteSupport } from '../../context/EngineerRemoteSupportProvider';
import { UserInfoCard } from './components/UserInfoCard';
import { SessionStatusPanel } from './components/SessionStatusPanel';
import { RemoteSessionView } from './components/RemoteSessionView';
import { searchSupportUser } from '../../services/supportApi';

export default function RemoteSupportPage() {
  const [searchParams] = useSearchParams();
  const [organizationCode, setOrganizationCode] = useState('');
  const [userId, setUserId] = useState('');
  const [user, setUser] = useState<Awaited<ReturnType<typeof searchSupportUser>> | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const {
    session,
    phase,
    loading,
    error,
    remoteVideoRef,
    loadSession,
    disconnect,
    sendControlEvent,
    clearError,
  } = useEngineerRemoteSupport();

  useEffect(() => {
    const sessionId = searchParams.get('sessionId');
    if (sessionId) void loadSession(sessionId);
  }, [searchParams.toString(), loadSession]);

  const handleSearch = async () => {
    clearError();
    if (!organizationCode.trim() || !userId.trim()) return;
    setSearchLoading(true);
    try {
      const result = await searchSupportUser(organizationCode.trim(), userId.trim());
      setUser(result);
    } catch {
      setUser(null);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">Remote Customer Support</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          View the customer portal after they approve a remote support request from chat.
        </p>
      </div>

      {session && (
        <>
          <SessionStatusPanel
            session={session}
            phase={phase}
            loading={loading}
            onDisconnect={() => disconnect('Ended by support agent')}
          />
          {(phase === 'connected' || phase === 'waiting') && (
            <RemoteSessionView
              session={session}
              remoteVideoRef={remoteVideoRef}
              onControlClick={(x, y) => sendControlEvent({ type: 'click', x, y })}
            />
          )}
          {phase === 'waiting' && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
              Waiting for the customer to approve and share their screen…
            </div>
          )}
          {phase === 'connected' && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
              Remote session active — you should see the customer&apos;s shared browser tab below.
            </div>
          )}
        </>
      )}

      {!session && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 space-y-4">
          <h2 className="font-semibold text-[hsl(var(--foreground))] flex items-center gap-2">
            <MdSupportAgent className="text-lg" />
            Lookup customer (optional)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="space-y-1 text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">Organization Code</span>
              <input
                value={organizationCode}
                onChange={(e) => setOrganizationCode(e.target.value)}
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">User ID</span>
              <input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 font-mono text-xs"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={searchLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            <MdSearch className="text-lg" />
            Search User
          </button>
          {user && <UserInfoCard user={user} />}
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Tip: Request remote access from the support chat widget — the session opens here automatically when the customer approves.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}
